import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import MonthlyCard from '../components/cards/MonthlyCard';
import MonthlyFolderCard from '../components/common/MonthlyFolderCard';
import EmptyState from '../components/common/EmptyState';
import { FiFilm, FiInfo, FiChevronLeft, FiFolder } from 'react-icons/fi';

const TourVideo = () => {
  const { monthlyUpdates = [] } = useOutletContext() || {};
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);

  // ✅ Group non-360 videos by month/year
  const months = monthlyUpdates.reduce((acc, update) => {
    const videos = update.videos?.filter(v => v.video_type !== '360' && !v.is_360) || [];
    if (videos.length > 0) {
      const folderSet = new Set(videos.map(v => v.custom_folder_name || 'General'));
      acc.push({
        id: `${update.update_id}-video`,
        updateId: update.update_id,
        month: update.month,
        year: update.year,
        videos: videos,
        count: videos.length,
        folderCount: folderSet.size,
        progress: update.progress_percentage,
        notes: update.notes,
      });
    }
    return acc;
  }, []).sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.month - a.month;
  });

  // ✅ Group by custom folder name
  const folders = selectedMonth
    ? Object.values(
      selectedMonth.videos.reduce((acc, vid) => {
        const key = vid.custom_folder_name || 'General';
        if (!acc[key]) {
          acc[key] = { folderName: key, videos: [] };
        }
        acc[key].videos.push(vid);
        return acc;
      }, {})
    )
    : [];

  const handleOpenVideo = (video) => {
    if (video.video_url) {
      window.open(video.video_url, '_blank');
    }
  };

  const getMonthName = (month) => {
    const names = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return names[month - 1] || `Month ${month}`;
  };

  // ✅ LEVEL 3: Videos inside folder
  if (selectedFolder) {
    return (
      <div>
        <button
          onClick={() => setSelectedFolder(null)}
          className="btn-secondary py-sm px-md flex items-center gap-xs text-sm mb-md text-textPrimary hover:bg-bgLight"
        >
          <FiChevronLeft />
          Back to folders
        </button>

        <div className="section-title">
          <div className="section-title__icon"><FiFolder /></div>
          <div>
            <h2>{selectedFolder.folderName}</h2>
            <p>{selectedFolder.videos.length} video{selectedFolder.videos.length !== 1 ? 's' : ''} available</p>
          </div>
        </div>

        <div className="portal-grid portal-grid--three">
          {selectedFolder.videos.map((vid) => (
            <MonthlyCard
              key={vid.video_id}
              month={selectedMonth.month}
              year={selectedMonth.year}
              title={vid.title}
              progress={selectedMonth.progress}
              notes={vid.notes}
              thumbnail={vid.thumbnail_url || "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=800"}
              type="video"
              onClick={() => handleOpenVideo(vid)}
            />
          ))}
        </div>
      </div>
    );
  }

  // ✅ LEVEL 2: Folders inside month
  if (selectedMonth) {
    return (
      <div>
        <button
          onClick={() => setSelectedMonth(null)}
          className="btn-secondary py-sm px-md flex items-center gap-xs text-sm mb-md text-textPrimary hover:bg-bgLight"
        >
          <FiChevronLeft />
          Back to all months
        </button>

        <div className="section-title">
          <div className="section-title__icon"><FiFilm /></div>
          <div>
            <h2>Site Videos - {getMonthName(selectedMonth.month)} {selectedMonth.year}</h2>
            <p>{folders.length} folder{folders.length !== 1 ? 's' : ''} • {selectedMonth.count} videos total</p>
          </div>
        </div>

        <div className="space-y-3">
          {folders.map((folder) => (
            <MonthlyFolderCard
              key={folder.folderName}
              month={selectedMonth.month}
              year={selectedMonth.year}
              customName={folder.folderName}
              count={folder.videos.length}
              isVideo={true}
              onClick={() => setSelectedFolder(folder)}
            />
          ))}
        </div>
      </div>
    );
  }

  // ✅ LEVEL 1: Months
  return (
    <div>
      <div className="section-title">
        <div className="section-title__icon"><FiFilm /></div>
        <div>
          <h2>Site Videos</h2>
          <p>Select any month to view site videos.</p>
        </div>
      </div>

      {months.length === 0 ? (
        <EmptyState message="No tracking videos have been uploaded for this site yet." />
      ) : (
        <div className="space-y-3">
          {months.map((month) => (
            <MonthlyFolderCard
              key={month.id}
              month={month.month}
              year={month.year}
              count={month.count}
              summary={`${month.folderCount} folder${month.folderCount !== 1 ? 's' : ''}, ${month.count} video${month.count !== 1 ? 's' : ''}`}
              isVideo={true}
              onClick={() => setSelectedMonth(month)}
            />
          ))}
        </div>
      )}

      {months.length > 0 && (
        <div className="info-bar mt-md">
          <FiInfo /> Click any month folder to view videos.
        </div>
      )}
    </div>
  );
};

export default TourVideo;